/*
 * Account Ticket Credit — read seam + pure formatters (the Apex postponement
 * retention credit: single-use code worth 50% of spend, 12 months, locked to
 * the purchaser's email; ruling: visible on each user's account).
 *
 * Wire: my-api GET /me/ticket-credits (Bearer ID token, same as /me/history).
 *   200 {available:true,  credits:[…]}  → one card per credit, in API order
 *   200 {available:false, credits:[]}   → one "couldn't load" line
 *   404 (server flag off)               → nothing (same as no credits)
 * Mirrors fetchMyHistory: every other failure (network error, other non-200,
 * non-JSON or malformed body) degrades to null and the section renders
 * NOTHING — never an error banner.
 *
 * "Ticket Credit" here is ACCOUNT-level; it is unrelated to the seat status of
 * the same name elsewhere in the programme (hence the account-ticket-credit-*
 * test ids).
 */
import { useEffect, useState } from 'react'
import { loadSession } from '../auth/session'
import { ticketCreditsEnabled } from '../lib/ticketCreditsFlag'
import type { Lang } from '../i18n/useLocale'
import { myApiBase } from './myApi'
import { formatPlainDate } from './legacyHistory'

export type TicketCreditStatus = 'active' | 'redeemed' | 'expired' | 'revoked'

export interface TicketCredit {
  /** Only for an active credit; null once it can't be used (my-api already
   *  withholds dead codes; any that slip through are dropped here too). */
  code: string | null
  label: string
  amount_minor: number
  currency_iso: string
  /** ISO 8601 instant (or plain YYYY-MM-DD). */
  expires_at: string
  status: TicketCreditStatus
  redeemed_order_id: string | null
  for_event_name: string | null
  brand_names: string[]
}

export interface TicketCreditsView {
  available: boolean
  credits: TicketCredit[]
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0
const isNullableString = (v: unknown): v is string | null => v === null || typeof v === 'string'

function parseCredit(raw: unknown): TicketCredit | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const c = raw as Record<string, unknown>
  if (!isCount(c.amount_minor)) return null
  if (typeof c.currency_iso !== 'string' || !/^[A-Z]{3}$/.test(c.currency_iso)) return null
  if (typeof c.expires_at !== 'string') return null
  if (c.status !== 'active' && c.status !== 'redeemed' && c.status !== 'expired' && c.status !== 'revoked') return null
  const active = c.status === 'active'
  if (active && (typeof c.code !== 'string' || c.code.trim() === '')) return null
  const redeemed = c.redeemed_order_id ?? null
  const forEvent = c.for_event_name ?? null
  if (!isNullableString(redeemed) || !isNullableString(forEvent)) return null
  const brands = Array.isArray(c.brand_names)
    ? (c.brand_names as unknown[]).filter((b): b is string => typeof b === 'string' && b.trim() !== '')
    : []
  return {
    code: active ? (c.code as string) : null,
    label: typeof c.label === 'string' ? c.label : '',
    amount_minor: c.amount_minor,
    currency_iso: c.currency_iso,
    expires_at: c.expires_at,
    status: c.status,
    redeemed_order_id: redeemed || null,
    for_event_name: forEvent || null,
    brand_names: brands,
  }
}

/** DEFENSIVE parse of a GET /me/ticket-credits body. A malformed envelope ⇒
 *  null (render nothing); a single malformed credit is dropped, never guessed. */
export function parseTicketCredits(raw: unknown): TicketCreditsView | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const b = raw as Record<string, unknown>
  if (typeof b.available !== 'boolean' || !Array.isArray(b.credits)) return null
  const credits = (b.credits as unknown[]).map(parseCredit).filter((c): c is TicketCredit => c !== null)
  return { available: b.available, credits }
}

/** GET /me/ticket-credits — NEVER throws. 404 (server flag off) and every
 *  other failure ⇒ null (render nothing). */
export async function fetchMyTicketCredits(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TicketCreditsView | null> {
  try {
    const res = await fetchImpl(`${base.replace(/\/$/, '')}/me/ticket-credits`, {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    if (res.status !== 200) return null
    return parseTicketCredits(await res.json())
  } catch {
    return null
  }
}

/** Read the caller's ticket credits once per mount. null until (and unless) a
 *  well-formed 200 arrives; flag off ⇒ no request at all. */
export function useTicketCredits(env: Record<string, string | undefined> = import.meta.env): TicketCreditsView | null {
  const enabled = ticketCreditsEnabled(env)
  const base = myApiBase(env)
  const [view, setView] = useState<TicketCreditsView | null>(null)
  useEffect(() => {
    if (!enabled || !base) return
    const session = loadSession()
    if (!session) return
    let cancelled = false
    void fetchMyTicketCredits(session.idToken, base).then((v) => {
      if (!cancelled) setView(v)
    })
    return () => { cancelled = true }
  }, [enabled, base])
  return enabled ? view : null
}

/** "Use by" date. A plain YYYY-MM-DD is a calendar date (formatPlainDate, UTC);
 *  a full ISO instant is shown as the calendar date in the viewer's own
 *  timezone. Unparseable ⇒ the raw value (honest, never invented). */
export function formatUseBy(expiresAt: string, lang: Lang): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(expiresAt)) return formatPlainDate(expiresAt, lang)
  const d = new Date(expiresAt)
  if (Number.isNaN(d.getTime())) return expiresAt
  return d.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
